import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import tw from 'twin.macro';

import { Loader } from '@components/Loader';
import { Spacer } from '@components/Spacer';
import { Modal, ModalContent, ModalHeader } from '@components/Modal';
import { Input } from '@auth_module/components/Input';
import { createApiKey } from '@account_module/services/selfServeApi';
import { useUser } from '@auth_module/hooks/useUser';
import { SELF_SERVE_API } from '@account_module/constants/selfServeApiKeys';
import { CloseIcon } from '@icons/CloseIcon';
import { useCustomerContext } from '@account_module/context/CustomerContext';

type GenerateApiKeyForm = {
  name: string;
};

const Form = tw.form`flex flex-col gap-2 min-w-[531px]`;
const ErrorMessage = tw.span`text-left text-error-text-icon`;
const StyledButton = tw.button`bg-accent-2 w-auto h-[32px] rounded-[6px] disabled:opacity-50 text-[16px] leading-[100%] px-4 text-text-and-icon-invert disabled:cursor-no-drop`;
const FormButton = tw(StyledButton)`rounded-[4px] h-[36px] flex items-center justify-center`;
const CancelButton = tw(
  FormButton
)`bg-transparent! text-text-and-icon-muted border border-solid border-gray-1`;
const ButtonsContainer = tw.div`flex items-center justify-end gap-2`;
const Label = tw.label`text-[14px] text-text-and-icon-muted font-semibold leading-[150%] block mb-[8px]`;
const ModalTitle = tw.span`text-[16px] text-text-and-icon font-semibold leading-[140%]`;

const GenerateApiKeyForm = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isFormVisible, setIsFormVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { organization } = useUser();
  const { customer } = useCustomerContext();
  const GenerateApiKeySchema = yup.object().shape({
    name: yup.string().required('Name is required').max(100),
  });
  const { register, handleSubmit, formState, reset } = useForm<GenerateApiKeyForm>({
    resolver: yupResolver(GenerateApiKeySchema),
  });

  const { errors } = formState;

  const onSubmit = (formData: GenerateApiKeyForm) => {
    if (customer?.can_create !== 1) return;

    setIsLoading(true);

    createApiKey({ name: formData.name, organization_id: organization.id })
      .then(() => {
        queryClient.invalidateQueries([SELF_SERVE_API.KEYS]);
        setIsFormVisible(false);
        reset();
      })
      .catch((error) => console.error('Create API error', error))
      .finally(() => setIsLoading(false));
  };

  const handleClose = () => {
    setIsFormVisible(false);
    reset();
  };

  return (
    <>
      <StyledButton onClick={() => setIsFormVisible(true)} disabled={customer?.can_create !== 1}>
        {t(
          'creator_dashboard_module.self_serve_api_page.generate_api_key_form.generate_key_button'
        )}
      </StyledButton>
      {isFormVisible && (
        <Modal overlayStyles={tw`bg-muted-transparent`} onClose={handleClose}>
          <div>
            <ModalHeader tw='px-4'>
              <ModalTitle>
                {t('creator_dashboard_module.self_serve_api_page.generate_api_key_form.title')}
              </ModalTitle>
              <CloseIcon onClick={handleClose} tw='fill-text-and-icon cursor-pointer' />
            </ModalHeader>
            <ModalContent tw='px-4'>
              <Spacer direction='vertical' size='md' />
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Label htmlFor='name'>
                  {t(
                    'creator_dashboard_module.self_serve_api_page.generate_api_key_form.input.label'
                  )}
                </Label>
                <Input
                  type='text'
                  id='name'
                  placeholder={t(
                    'creator_dashboard_module.self_serve_api_page.generate_api_key_form.input.placeholder'
                  )}
                  {...register('name')}
                />
                {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}

                <Spacer direction='vertical' size='md' />
                <ButtonsContainer>
                  <CancelButton type='button' onClick={handleClose}>
                    {t('common.cancel')}
                  </CancelButton>
                  <FormButton type='submit'>
                    {isLoading ? (
                      <Loader theme='dark' size='sm' />
                    ) : (
                      t('creator_dashboard_module.self_serve_api_page.generate_api_key_form.cta')
                    )}
                  </FormButton>
                </ButtonsContainer>
              </Form>
            </ModalContent>
          </div>
        </Modal>
      )}
    </>
  );
};

export { GenerateApiKeyForm };
